// Seed the user's real cloud account with the curated sample library
// (8 folders + 16 docs from demoData.js). Used by:
//   - First-sign-in modal when the user opts into a sample library
//   - Settings "Reset my account" button
//
// Real cloud writes via apiClient.upsertFolder + upsertDoc — once seeded,
// the user can edit, append to, or delete anything as if they made it
// themselves. Claude's vault-aware synthesizer will treat these folders
// as in-inventory and route future captures into them.

import { apiClient } from './apiClient.js';
import { DEMO_FOLDERS, DEMO_DOC_DETAILS } from './demoData.js';
import { captureError } from './sentry.js';

export async function seedLibrary({ onProgress } = {}) {
  if (!apiClient.enabled) {
    throw new Error('Sign in required to seed library.');
  }
  const progress = onProgress || (() => {});
  let folders = 0;
  let docs = 0;
  let failed = 0;

  progress({ stage: 'folders', folders: 0, total: DEMO_FOLDERS.length });
  for (const folder of DEMO_FOLDERS) {
    try {
      await apiClient.upsertFolder({
        key: folder.key,
        title: folder.title,
        description: folder.description || '',
        private: false,
        hidden: false,
      });
      folders += 1;
      progress({ stage: 'folders', folders, total: DEMO_FOLDERS.length });
    } catch (e) {
      failed += 1;
      captureError(e, { stage: 'seedLibrary:upsertFolder', key: folder.key });
    }
  }

  const allDocs = [];
  for (const folderKey of Object.keys(DEMO_DOC_DETAILS)) {
    for (const docKey of Object.keys(DEMO_DOC_DETAILS[folderKey])) {
      allDocs.push(DEMO_DOC_DETAILS[folderKey][docKey]);
    }
  }

  progress({ stage: 'docs', docs: 0, total: allDocs.length });
  for (const doc of allDocs) {
    try {
      await apiClient.upsertDoc({
        key: doc.key,
        folderKey: doc.folderKey,
        title: doc.title,
        preview: doc.preview || '',
        body: doc.body || '',
        meta: doc.meta || '',
        sessions: Array.isArray(doc.sessions) ? doc.sessions : [],
        tags: Array.isArray(doc.tags) ? doc.tags : [],
      });
      docs += 1;
      progress({ stage: 'docs', docs, total: allDocs.length });
    } catch (e) {
      failed += 1;
      captureError(e, { stage: 'seedLibrary:upsertDoc', key: doc.key });
    }
  }

  return { folders, docs, failed };
}

// Delete every capture in the user's local IDB and (if signed in) on the
// cloud. Used by the "Reset my account" Settings button. The caller is
// responsible for re-rendering after this resolves.
//
// Signature: clearAllCaptures({ state, deleteFromDb, loadCaptures })
//   state         — the app.js state object (has .captures array)
//   deleteFromDb  — app.js's IDB delete helper
//   loadCaptures  — app.js's reloader (refreshes state.captures from IDB)
//
// Returns: count of captures cleared.
export async function clearAllCaptures({ state, deleteFromDb, loadCaptures }) {
  const ids = state.captures.map((c) => c.id);
  for (const id of ids) {
    try { await deleteFromDb(id); } catch (e) { captureError(e, { stage: 'clearAllCaptures:deleteFromDb', id }); }
    if (apiClient.enabled) {
      apiClient.deleteSession(id).catch((e) => captureError(e, { stage: 'clearAllCaptures:deleteSession', id }));
    }
  }
  await loadCaptures();
  return ids.length;
}
